import { useQueryClient } from '@tanstack/react-query';
import { useCallback } from 'react';
import { useAuth } from '@clerk/react';
import { useQuery, useMutation } from '@tanstack/react-query';

export interface WatchlistItem {
  id: number;
  marketId: string;
  platform: 'polymarket' | 'kalshi';
  marketQuestion: string;
  addedAt: string;
}

const WATCHLIST_KEY = ['watchlist'];

async function fetchWithAuth(url: string, token: string | null, options?: RequestInit) {
  const res = await fetch(url, {
    ...options,
    headers: {
      'Content-Type': 'application/json',
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
      ...options?.headers,
    },
  });
  if (!res.ok) throw new Error(`${res.status} ${res.statusText}`);
  return res;
}

export function useWatchlist() {
  const { getToken, isSignedIn } = useAuth();
  const qc = useQueryClient();

  const { data, isLoading } = useQuery({
    queryKey: WATCHLIST_KEY,
    enabled: !!isSignedIn,
    queryFn: async () => {
      const token = await getToken();
      const res = await fetchWithAuth('/api/watchlist', token);
      const json = await res.json();
      return json.items as WatchlistItem[];
    },
  });

  const addMutation = useMutation({
    mutationFn: async (item: { marketId: string; platform: string; marketQuestion: string }) => {
      const token = await getToken();
      const res = await fetchWithAuth('/api/watchlist', token, {
        method: 'POST',
        body: JSON.stringify(item),
      });
      return res.json() as Promise<WatchlistItem>;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: WATCHLIST_KEY }),
  });

  const removeMutation = useMutation({
    mutationFn: async (id: number) => {
      const token = await getToken();
      await fetchWithAuth(`/api/watchlist/${id}`, token, { method: 'DELETE' });
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: WATCHLIST_KEY }),
  });

  const isWatching = useCallback(
    (marketId: string, platform: string) =>
      data?.some((i) => i.marketId === marketId && i.platform === platform) ?? false,
    [data],
  );

  const getWatchlistId = useCallback(
    (marketId: string, platform: string) =>
      data?.find((i) => i.marketId === marketId && i.platform === platform)?.id ?? null,
    [data],
  );

  return {
    items: data ?? [],
    isLoading,
    isWatching,
    getWatchlistId,
    addToWatchlist: addMutation.mutateAsync,
    removeFromWatchlist: removeMutation.mutateAsync,
    isAdding: addMutation.isPending,
    isRemoving: removeMutation.isPending,
  };
}
